import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config';
import { api, fetchWithCSRF } from '../../utils/apiUtils';

// Async thunk for fetching personal workspaces
export const fetchWorkspaces = createAsyncThunk(
    'workspaces/fetchWorkspaces',
    async ({ useCache = false } = {}, { rejectWithValue }) => {
        try {
            // Use the enhanced API utility with caching
            return await api.get(
                `${config.API_URI}/v1/workspaces/me`,
                {},
                useCache,
                5 * 60 * 1000 // Cache for 5 minutes
            );
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch workspaces');
        }
    }
);
export const fetchLiteWorkspaces = createAsyncThunk(
    'workspaces/fetchLiteWorkspaces',
    async (_, { getState }) => {
        const response = await fetchWithCSRF(`${config.API_URI}/v1/workspaces/lite/me`, {
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Failed to fetch workspaces');
        }

        const data = await response.json();
        return data;
    }
);

// Async thunk for fetching guest workspaces
export const fetchGuestsWorkspaces = createAsyncThunk(
    'workspaces/fetchGuestsWorkspaces',
    async (_, { getState }) => {
        const response = await fetchWithCSRF(`${config.API_URI}/v1/workspaces/guests/me`, {
            credentials: 'include',
            headers: {
            }
        });

        if (!response.ok) {
            throw new Error('Failed to fetch guest workspaces');
        }

        const data = await response.json();
        return data;
    }
);

// Async thunk for updating workspace
export const updateWorkspace = createAsyncThunk(
    'workspaces/updateWorkspace',
    async ({ workspaceId, name }, { getState, rejectWithValue }) => {
        try {
            const response = await fetch(`${config.API_URI}/v1/workspaces/${workspaceId}`, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ name })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update workspace');
            }

            const data = await response.json();
            return { workspaceId, updatedWorkspace: data.workspace };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Async thunk for updating workspace order
export const updateWorkspaceOrder = createAsyncThunk(
    'workspaces/updateWorkspaceOrder',
    async (workspaceOrder, { rejectWithValue }) => {
        try {
            const response = await fetch(`${config.API_URI}/v1/users/me/workspace-order`, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ workspaceOrder })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update workspace order');
            }

            const data = await response.json();
            return data.workspaceOrder;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Redux slice for managing both types of workspaces
const workspaceSlice = createSlice({
    name: 'workspaces',
    initialState: {
        workspaces: [],
        guestWorkspaces: [],
        liteWorkspaces: [],
        status: 'idle',
        guestStatus: 'idle',
        updateStatus: 'idle',
        error: null
    },
    reducers: {
        // Local action for optimistic workspace reordering
        reorderWorkspaces: (state, action) => {
            state.workspaces = action.payload;
        },

        // ========== REAL-TIME OPTIMISTIC ACTIONS ==========

        // Add workspace from real-time event
        optimisticAddWorkspace: (state, action) => {
            const workspace = action.payload;
            // Check if workspace already exists (avoid duplicates)
            const exists = state.workspaces.some(ws => ws._id === workspace._id);
            if (!exists) {
                state.workspaces.push(workspace);
            }
        },

        // Update workspace from real-time event
        optimisticUpdateWorkspace: (state, action) => {
            const { workspaceId, updates } = action.payload;

            // Update in workspaces array
            const workspaceIndex = state.workspaces.findIndex(ws => ws._id === workspaceId);
            if (workspaceIndex !== -1) {
                state.workspaces[workspaceIndex] = {
                    ...state.workspaces[workspaceIndex],
                    ...updates
                };
            }

            // Update in guestWorkspaces array if it exists there
            const guestWorkspaceIndex = state.guestWorkspaces.findIndex(ws => ws._id === workspaceId);
            if (guestWorkspaceIndex !== -1) {
                state.guestWorkspaces[guestWorkspaceIndex] = {
                    ...state.guestWorkspaces[guestWorkspaceIndex],
                    ...updates
                };
            }
        },

        // Delete workspace from real-time event
        optimisticDeleteWorkspace: (state, action) => {
            const workspaceId = action.payload;

            // Remove from workspaces array (match by both _id and shortId)
            state.workspaces = state.workspaces.filter(ws =>
                ws._id !== workspaceId && ws.shortId !== workspaceId
            );

            // Remove from guestWorkspaces array (match by both _id and shortId)
            state.guestWorkspaces = state.guestWorkspaces.filter(ws =>
                ws._id !== workspaceId && ws.shortId !== workspaceId
            );
        },

        // Add member to workspace from real-time event
        optimisticAddWorkspaceMember: (state, action) => {
            const { workspaceId, member } = action.payload;

            // Update in workspaces array
            const workspace = state.workspaces.find(ws => ws._id === workspaceId);
            if (workspace) {
                if (!workspace.members) workspace.members = [];
                const exists = workspace.members.some(m => m._id === member._id || m.user === member._id);
                if (!exists) {
                    workspace.members.push(member);
                }
            }

            // Update in guestWorkspaces array
            const guestWorkspace = state.guestWorkspaces.find(ws => ws._id === workspaceId);
            if (guestWorkspace) {
                if (!guestWorkspace.members) guestWorkspace.members = [];
                const exists = guestWorkspace.members.some(m => m._id === member._id || m.user === member._id);
                if (!exists) {
                    guestWorkspace.members.push(member);
                }
            }
        },

        // Remove member from workspace from real-time event
        optimisticRemoveWorkspaceMember: (state, action) => {
            const { workspaceId, memberId } = action.payload;

            // Update in workspaces array
            const workspace = state.workspaces.find(ws => ws._id === workspaceId);
            if (workspace && workspace.members) {
                workspace.members = workspace.members.filter(m =>
                    m._id !== memberId && m.user !== memberId
                );
            }

            // Update in guestWorkspaces array
            const guestWorkspace = state.guestWorkspaces.find(ws => ws._id === workspaceId);
            if (guestWorkspace && guestWorkspace.members) {
                guestWorkspace.members = guestWorkspace.members.filter(m =>
                    m._id !== memberId && m.user !== memberId
                );
            }
        },

        // ========== BOARD OPERATIONS WITHIN WORKSPACES ==========

        // Add board to workspace from real-time event
        optimisticAddBoardToWorkspace: (state, action) => {
            const { workspaceId, board } = action.payload;

            // Helper function to add board to workspace
            const addBoardToWorkspace = (workspace) => {
                if (!workspace.boards) workspace.boards = [];

                // Check if board already exists (avoid duplicates)
                const exists = workspace.boards.some(
                    b => b._id === board._id || b.shortId === board.shortId
                );

                if (!exists) {
                    // Add to beginning of boards array (most recent)
                    workspace.boards.unshift(board);
                }
            };

            // Update in workspaces array
            const workspace = state.workspaces.find(
                ws => ws._id === workspaceId || ws.shortId === workspaceId
            );
            if (workspace) {
                addBoardToWorkspace(workspace);
            }

            // Update in guestWorkspaces array
            const guestWorkspace = state.guestWorkspaces.find(
                ws => ws._id === workspaceId || ws.shortId === workspaceId
            );
            if (guestWorkspace) {
                addBoardToWorkspace(guestWorkspace);
            }
        },

        // Update board in workspace from real-time event
        optimisticUpdateBoardInWorkspace: (state, action) => {
            const { boardId, updates } = action.payload;

            // Helper function to update board in workspace
            const updateBoardInWorkspace = (workspace) => {
                if (!workspace.boards) return;

                const boardIndex = workspace.boards.findIndex(
                    b => b._id === boardId || b.shortId === boardId
                );

                if (boardIndex !== -1) {
                    workspace.boards[boardIndex] = {
                        ...workspace.boards[boardIndex],
                        ...updates
                    };
                }
            };

            // Update in all workspaces
            state.workspaces.forEach(workspace => {
                updateBoardInWorkspace(workspace);
            });

            // Update in all guestWorkspaces
            state.guestWorkspaces.forEach(workspace => {
                updateBoardInWorkspace(workspace);
            });
        },

        // Remove board from workspace from real-time event
        optimisticRemoveBoardFromWorkspace: (state, action) => {
            const boardId = action.payload;

            // Helper function to remove board from workspace
            const removeBoardFromWorkspace = (workspace) => {
                if (!workspace.boards) return;

                workspace.boards = workspace.boards.filter(
                    b => b._id !== boardId && b.shortId !== boardId
                );
            };

            // Remove from all workspaces
            state.workspaces.forEach(workspace => {
                removeBoardFromWorkspace(workspace);
            });

            // Remove from all guestWorkspaces
            state.guestWorkspaces.forEach(workspace => {
                removeBoardFromWorkspace(workspace);
            });
        },

        // Add guest workspace from real-time event (when user is added to a board as a guest)
        optimisticAddGuestWorkspace: (state, action) => {
            const workspace = action.payload;

            // Check if workspace already exists in guestWorkspaces
            const existsInGuest = state.guestWorkspaces.some(
                ws => ws._id === workspace._id || ws.shortId === workspace.shortId
            );

            // Check if workspace exists in regular workspaces (user might be a member)
            const existsInRegular = state.workspaces.some(
                ws => ws._id === workspace._id || ws.shortId === workspace.shortId
            );

            // Only add to guestWorkspaces if it doesn't exist anywhere
            if (!existsInGuest && !existsInRegular) {
                state.guestWorkspaces.unshift(workspace);
            }
        },

        // Remove guest workspace from real-time event (when user is removed from all boards in workspace)
        optimisticRemoveGuestWorkspace: (state, action) => {
            const workspaceId = action.payload;

            // Remove from guestWorkspaces array (match by both _id and shortId)
            state.guestWorkspaces = state.guestWorkspaces.filter(ws =>
                ws._id !== workspaceId && ws.shortId !== workspaceId
            );
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch personal workspaces
            .addCase(fetchWorkspaces.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchWorkspaces.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.workspaces = action.payload;
            })
            .addCase(fetchWorkspaces.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(fetchLiteWorkspaces.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchLiteWorkspaces.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.liteWorkspaces = action.payload;
            })
            .addCase(fetchLiteWorkspaces.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })

            // Fetch guest workspaces
            .addCase(fetchGuestsWorkspaces.pending, (state) => {
                state.guestStatus = 'loading';
            })
            .addCase(fetchGuestsWorkspaces.fulfilled, (state, action) => {
                state.guestStatus = 'succeeded';
                state.guestWorkspaces = action.payload;
            })
            .addCase(fetchGuestsWorkspaces.rejected, (state, action) => {
                state.guestStatus = 'failed';
                state.error = action.error.message;
            })
            // Update workspace
            .addCase(updateWorkspace.pending, (state) => {
                state.updateStatus = 'loading';
            })
            .addCase(updateWorkspace.fulfilled, (state, action) => {
                state.updateStatus = 'succeeded';
                const { workspaceId, updatedWorkspace } = action.payload;

                // Update workspace in workspaces array (workspaceId is shortId)
                const workspaceIndex = state.workspaces.findIndex(ws => ws.shortId === workspaceId);
                if (workspaceIndex !== -1) {
                    state.workspaces[workspaceIndex] = { ...state.workspaces[workspaceIndex], ...updatedWorkspace };
                }

                // Update workspace in guestWorkspaces array if it exists there
                const guestWorkspaceIndex = state.guestWorkspaces.findIndex(ws => ws.shortId === workspaceId);
                if (guestWorkspaceIndex !== -1) {
                    state.guestWorkspaces[guestWorkspaceIndex] = { ...state.guestWorkspaces[guestWorkspaceIndex], ...updatedWorkspace };
                }
            })
            .addCase(updateWorkspace.rejected, (state, action) => {
                state.updateStatus = 'failed';
                state.error = action.payload;
            })
            // Update workspace order
            .addCase(updateWorkspaceOrder.pending, (state) => {
                state.updateStatus = 'loading';
                // Store the current order in case we need to revert
                state.previousOrder = [...state.workspaces];
            })
            .addCase(updateWorkspaceOrder.fulfilled, (state, action) => {
                state.updateStatus = 'succeeded';
                state.previousOrder = null; // Clear the backup
            })
            .addCase(updateWorkspaceOrder.rejected, (state, action) => {
                state.updateStatus = 'failed';
                state.error = action.payload;
                // Revert to previous order if the update failed
                if (state.previousOrder) {
                    state.workspaces = state.previousOrder;
                }
                state.previousOrder = null;
            });
    }
});

export const {
    reorderWorkspaces,
    // Real-time optimistic actions
    optimisticAddWorkspace,
    optimisticUpdateWorkspace,
    optimisticDeleteWorkspace,
    optimisticAddWorkspaceMember,
    optimisticRemoveWorkspaceMember,
    // Board operations within workspaces
    optimisticAddBoardToWorkspace,
    optimisticUpdateBoardInWorkspace,
    optimisticRemoveBoardFromWorkspace,
    // Guest workspace operations
    optimisticAddGuestWorkspace,
    optimisticRemoveGuestWorkspace,
} = workspaceSlice.actions;
export default workspaceSlice.reducer;
